import { AnimatePresence, motion } from "framer-motion";
import * as React from "react";
import { useLocation, useOutlet } from "react-router-dom";
import { Footer } from "@/design-system";
import { AppShellProvider } from "@/design-system/ThemeProvider";
import { LandscapeProvider } from "@/landscape";
import { Header } from "./Header";

export function Layout() {
  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <LandscapeProvider>
      <div className="relative min-h-dvh overflow-x-clip">
        <a
          href="#main-content"
          className="sr-only z-ds-tooltip rounded-[var(--radius-md)] bg-ds-gold-primary px-4 py-2 font-heading text-ds-text-inverse focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
        >
          Skip to content
        </a>
        <div
          className="fixed inset-x-0 top-0 z-[55] h-0.5 bg-gradient-to-r from-ds-bronze-primary via-ds-gold-accent to-ds-bronze-primary"
          style={{
            backgroundSize: "200% 100%",
            backgroundPosition: "0% 0%",
            backgroundAttachment: "fixed",
          }}
        />
        <Header />

        <main id="main-content" tabIndex={-1} className="focus:outline-none">
          <AppShellProvider>
            <AnimatePresence mode="wait">
              <PageTransition key={location.pathname}>
                <FrozenOutlet />
              </PageTransition>
            </AnimatePresence>
          </AppShellProvider>
        </main>

        <Footer />
      </div>
    </LandscapeProvider>
  );
}

/** Keeps the exiting page's content rendered while it animates out. */
function FrozenOutlet() {
  const outlet = useOutlet();
  const [frozen] = React.useState(outlet);
  return frozen;
}

/** Page-level enter/exit transition. */
export function PageTransition({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      exit={{ opacity: 0, y: -24, filter: "blur(8px)" }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}
